import { Box, Button, Drawer, Flex, Loader, Table, Text } from "@mantine/core";
import { MinusCircleIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSwr from "swr";
import { LateFeeApi } from "../../apis/late-fee";
import { AppModalHeader, ModalFooter } from "../ui/app-modal";
import { REGISTER_PAYMENT_DRAWER_STYLES } from "../register-payment/register-payment";
import { currencyFormat } from "../../utils";
import { dueDay } from "../../utils/due-day";
import { buildLateFeePeriods, formatPercent, isLateFeeCapped } from "./late-fee-periods";
import classes from "./late-fee-drawer.module.css";
import grid from "../ui/data-grid.module.css";

interface LateFeeDrawerProps {
  invoiceId: string;
  opened: boolean;
  onClose: () => void;
  onOpenReminder?: () => void;
}

interface IDailyBreakdown {
  day: number;
  rate: number;
  dailyFee: number;
  cumulativeFee: number;
}

interface IBreakdown {
  totalFee: number;
  penaltyDays: number;
  gracePeriodDays: number;
  feeCalculationType: string;
  dailyBreakdown: IDailyBreakdown[];
}

interface ILateFeeData {
  refCode?: string;
  totalAmount?: number;
  lateFee: number;
  daysPastDue: number;
  remainingAmount?: number;
  lateFeeEnabled: boolean;
  effectiveConfig?: {
    feeCalculationType: string;
    dailyPenaltyRate: number | null;
    monthlyPenaltyRate: number | null;
    flatFeeAmount: number | null;
    gracePeriodDays: number;
    maxPenaltyAmount?: number | null;
    maxPenaltyRate?: number | null;
  };
  breakdown: IBreakdown | null;
  overdueDate?: string | null;
  paymentDate?: string | null;
}

export function LateFeeDrawer({ invoiceId, opened, onClose, onOpenReminder }: LateFeeDrawerProps) {
  const { t } = useTranslation("settings");

  const calcTypeLabels: Record<string, string> = {
    DAILY_PERCENTAGE: t("lateFeeDrawer.calcDaily"),
    MONTHLY_PERCENTAGE: t("lateFeeDrawer.calcMonthly"),
    COMPOUND_DAILY: t("lateFeeDrawer.calcCompound"),
    FLAT_FEE: t("lateFeeDrawer.calcFlat"),
    ESCALATING: t("lateFeeDrawer.calcTiered"),
    FIXED_RATE: t("lateFeeDrawer.calcFixed"),
    COMPOUND: t("lateFeeDrawer.calcCompound"),
    TIERED: t("lateFeeDrawer.calcTiered"),
  };

  const { data, isLoading } = useSwr<ILateFeeData>(
    opened && invoiceId ? ["swr.late-fees", invoiceId] : null,
    () => LateFeeApi.getInvoiceLateFees(invoiceId),
    { revalidateOnFocus: false },
  );

  const grace = data?.effectiveConfig?.gracePeriodDays ?? data?.breakdown?.gracePeriodDays ?? 0;
  const billableDays = Math.max(0, (data?.daysPastDue || 0) - grace);
  const cfg = data?.effectiveConfig;
  const due = dueDay(data?.paymentDate);
  const capped = isLateFeeCapped(data?.lateFee || 0, data?.totalAmount || 0, cfg);
  const periods = due ? buildLateFeePeriods(due, grace, data?.breakdown?.dailyBreakdown || [], capped) : [];

  const calcLabel = (() => {
    const type = cfg?.feeCalculationType || "";
    const label = calcTypeLabels[type] || type || "-";
    switch (type) {
      case "DAILY_PERCENTAGE":
      case "COMPOUND_DAILY":
        return cfg?.dailyPenaltyRate != null ? `${label} · ${formatPercent(cfg.dailyPenaltyRate)}` : label;
      case "MONTHLY_PERCENTAGE":
        return cfg?.monthlyPenaltyRate != null ? `${label} · ${formatPercent(cfg.monthlyPenaltyRate)}` : label;
      case "FLAT_FEE":
        return cfg?.flatFeeAmount != null ? `${label} · ${currencyFormat(Number(cfg.flatFeeAmount))}` : label;
      default:
        return label;
    }
  })();

  const capParts: string[] = [];
  if (cfg?.maxPenaltyRate != null) capParts.push(formatPercent(cfg.maxPenaltyRate));
  if (cfg?.maxPenaltyAmount != null) capParts.push(currencyFormat(Number(cfg.maxPenaltyAmount)));
  const capLabel = capParts.length ? capParts.join(" / ") : "-";

  const subtitle = data?.refCode ? (
    <>
      <span className={classes.mono}>{data.refCode}</span>
      {" · "}
      {t("lateFeeDrawer.daysPastDueValue", { days: data.daysPastDue || 0 })}
    </>
  ) : undefined;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      size="md"
      withCloseButton={false}
      styles={REGISTER_PAYMENT_DRAWER_STYLES}
    >
      <Box className={classes.root}>
        <AppModalHeader bordered title={t("lateFeeDrawer.title")} subtitle={subtitle} onClose={onClose} />

        {isLoading ? (
          <Flex justify="center" p={40}>
            <Loader />
          </Flex>
        ) : !data ? (
          <Text className={classes.empty}>{t("lateFeeDrawer.noData")}</Text>
        ) : !data.lateFeeEnabled ? (
          <Box className={classes.body}>
            <Box className={classes.disabledState}>
              <span className={classes.disabledIcon}>
                <MinusCircleIcon size={20} weight="regular" />
              </span>
              <Text fw={600} size="md">
                {t("lateFeeDrawer.disabledTitle")}
              </Text>
              <Text size="sm" c="dimmed" maw={340}>
                {t("lateFeeDrawer.disabledBody")}
              </Text>
              <Button component={Link} to="/settings" variant="default" mt="xs">
                {t("lateFeeDrawer.settingsLink")}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box className={classes.body}>
            <Box className={classes.statGrid}>
              <Box className={classes.statCellDanger}>
                <Text className={classes.statLabel}>{t("lateFeeDrawer.accruedFee")}</Text>
                <Text className={classes.statValueDanger}>{currencyFormat(data.lateFee || 0)}</Text>
                {capped ? <Text className={classes.statHint}>{t("lateFeeDrawer.capReached")}</Text> : null}
              </Box>
              <Box className={classes.statCell}>
                <Text className={classes.statLabel}>{t("lateFeeDrawer.billableDays")}</Text>
                <Text className={classes.statValue}>
                  {billableDays} {t("lateFeeDrawer.daysSuffix")}
                </Text>
                {grace > 0 ? (
                  <Text className={classes.statHint}>
                    {t("lateFeeDrawer.billableHint", { days: data.daysPastDue || 0, grace })}
                  </Text>
                ) : null}
              </Box>
            </Box>

            <Box className={classes.infoRows}>
              <Box className={classes.infoRow}>
                <Text className={classes.infoLabel}>{t("lateFeeDrawer.baseAmount")}</Text>
                <Text className={classes.infoValue}>
                  {currencyFormat(data.remainingAmount || 0)}
                </Text>
              </Box>
              <Box className={classes.infoRow}>
                <Text className={classes.infoLabel}>{t("lateFeeDrawer.calcMethod")}</Text>
                <Text className={classes.infoValue}>
                  {calcLabel}
                </Text>
              </Box>
              <Box className={classes.infoRow}>
                <Text className={classes.infoLabel}>{t("lateFeeDrawer.cap")}</Text>
                <Text className={classes.infoValue}>
                  {capLabel}
                </Text>
              </Box>
              <Box className={classes.infoRow}>
                <Text className={classes.infoLabel}>{t("lateFeeDrawer.dueEnded")}</Text>
                <Text className={classes.infoValue}>
                  {due ?? "-"}
                </Text>
              </Box>
            </Box>

            {periods.length === 0 ? (
              <Text className={classes.empty}>{t("lateFeeDrawer.noDailyBreakdown")}</Text>
            ) : (
              <Box className={classes.tableWrap}>
                <Table className={grid.table} data-density="compact" horizontalSpacing={0} verticalSpacing={0} withRowBorders={false}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("lateFeeDrawer.colPeriod")}</Table.Th>
                      <Table.Th className={classes.num}>{t("lateFeeDrawer.colDay")}</Table.Th>
                      <Table.Th className={classes.num}>{t("lateFeeDrawer.colRate")}</Table.Th>
                      <Table.Th className={classes.num}>{t("lateFeeDrawer.colFee")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {periods.map((row, index) => (
                      <Table.Tr key={index}>
                        <Table.Td>
                          {row.from} - {row.to}
                          {row.kind === "grace" ? ` · ${t("lateFeeDrawer.graceLabel")}` : null}
                          {row.kind === "capped" ? ` · ${t("lateFeeDrawer.cappedSuffix")}` : null}
                        </Table.Td>
                        <Table.Td className={classes.num}>{row.days}</Table.Td>
                        <Table.Td className={`${classes.num} ${row.rate > 0 ? "" : classes.muted}`}>
                          {row.rate > 0 ? formatPercent(row.rate) : "-"}
                        </Table.Td>
                        <Table.Td
                          className={`${classes.num} ${row.kind === "grace" ? classes.muted : row.fee > 0 ? classes.strong : ""}`}
                        >
                          {currencyFormat(row.fee, { withSymbol: false })}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            )}
            <Text className={classes.footnote}>{t("lateFeeDrawer.footnote")}</Text>
          </Box>
        )}

        <ModalFooter sticky>
          <Button variant="default" onClick={onClose}>
            {t("lateFeeDrawer.close")}
          </Button>
          {data?.lateFeeEnabled && onOpenReminder ? (
            <Button variant="default" onClick={onOpenReminder}>
              {t("lateFeeDrawer.addToReminder")}
            </Button>
          ) : null}
        </ModalFooter>
      </Box>
    </Drawer>
  );
}
